<template>
    <div class="address-fields" v-click-outside="hideSuggestions">

        <!-- Adresse (numéro + rue) + Code postal -->
        <div class="address-row">
            <div class="field-group field-address">
                <input
                    :id="props.id ? `${props.id}-street` : undefined"
                    v-model="fields.street"
                    @input="onFieldInput('street')"
                    @keydown.escape="hideSuggestions"
                    @keydown.arrow-down.prevent="navigate(1)"
                    @keydown.arrow-up.prevent="navigate(-1)"
                    @keydown.enter.prevent="selectActive"
                    @blur="onBlur"
                    type="text"
                    placeholder="Numéro et rue"
                    autocomplete="off"
                    :class="{ 'input-error': hasError }"
                />
                <ul
                    v-if="activeSuggestionsField === 'street' && showSuggestions && suggestions.length"
                    class="suggestions"
                >
                    <li
                        v-for="(feature, index) in suggestions"
                        :key="feature.properties.id"
                        :class="{ active: index === activeIndex }"
                        @mousedown.prevent="selectSuggestion(feature, 'street')"
                        @mouseover="activeIndex = index"
                    >
                        <span class="suggestion-main">
                            <span v-if="feature.properties.housenumber">{{ feature.properties.housenumber }} </span>
                            <span>{{ feature.properties.street ?? feature.properties.name }}</span>
                        </span>
                        <span class="suggestion-sub">
                            {{ feature.properties.postcode }} {{ feature.properties.city }}
                        </span>
                    </li>
                </ul>
            </div>

            <!-- Code postal -->
            <div class="field-group field-postcode">
                <input
                    :id="props.id ? `${props.id}-postcode` : undefined"
                    v-model="fields.postcode"
                    @input="onFieldInput('postcode')"
                    @keydown.escape="hideSuggestions"
                    @keydown.arrow-down.prevent="navigate(1)"
                    @keydown.arrow-up.prevent="navigate(-1)"
                    @keydown.enter.prevent="selectActive"
                    @blur="onBlur"
                    type="text"
                    placeholder="Code postal"
                    autocomplete="off"
                    maxlength="5"
                    :readonly="!!fields.street && selectedAddress"
                    :class="{ 'input-error': hasError }"
                />
                <ul
                    v-if="activeSuggestionsField === 'postcode' && showSuggestions && suggestions.length"
                    class="suggestions"
                >
                    <li
                        v-for="(feature, index) in suggestions"
                        :key="feature.properties.id"
                        :class="{ active: index === activeIndex }"
                        @mousedown.prevent="selectSuggestion(feature, 'postcode')"
                        @mouseover="activeIndex = index"
                    >
                        <span class="suggestion-main">{{ feature.properties.postcode }}</span>
                        <span class="suggestion-sub">{{ feature.properties.city }}</span>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Ville -->
        <div class="field-group field-city">
            <input
                :id="props.id ? `${props.id}-city` : undefined"
                v-model="fields.city"
                @input="onFieldInput('city')"
                @keydown.escape="hideSuggestions"
                @keydown.arrow-down.prevent="navigate(1)"
                @keydown.arrow-up.prevent="navigate(-1)"
                @keydown.enter.prevent="selectActive"
                @blur="onBlur"
                type="text"
                placeholder="Ville"
                autocomplete="off"
                :readonly="!!fields.street && selectedAddress"
                :class="{ 'input-error': hasError }"
            />
            <ul
                v-if="activeSuggestionsField === 'city' && showSuggestions && suggestions.length"
                class="suggestions"
            >
                <li
                    v-for="(feature, index) in suggestions"
                    :key="feature.properties.id"
                    :class="{ active: index === activeIndex }"
                    @mousedown.prevent="selectSuggestion(feature, 'city')"
                    @mouseover="activeIndex = index"
                >
                    <span class="suggestion-main">{{ feature.properties.city }}</span>
                    <span class="suggestion-sub">{{ feature.properties.postcode }}</span>
                </li>
            </ul>
        </div>

    </div>
</template>

<script setup>
import apiClient            from '@/api'
import { ref, reactive }    from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
    id:       { type: String,  default: null },
    hasError: { type: Boolean, default: false },
})

const emits = defineEmits(['selected-address', 'blur'])

const fields = reactive({
    street: '',
    postcode: '',
    city: '',
})

const lastValidAddress = reactive({
    street: '',
    postcode: '',
    city: '',
})

const programmatic = reactive({
    street: false,
    postcode: false,
    city: false,
})

const suggestions            = ref([])
const showSuggestions        = ref(false)
const activeIndex            = ref(-1)
const activeSuggestionsField = ref(null)
const selectedAddress        = ref(null)

let debounceTimer = null

const API_TYPE = {
    street: 'housenumber',
    postcode: 'municipality',
    city: 'municipality',
}

const MIN_LENGTH = {
    street: 3,
    postcode: 4,
    city: 2,
}


async function fetchSuggestions(query, type) {
    const url =
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5&type=${type}`

    const response = await apiClient.get(url)

    return response.data.features
}


function onFieldInput(fieldName) {

    if (programmatic[fieldName]) {
        programmatic[fieldName] = false
        return
    }

    clearTimeout(debounceTimer)

    activeIndex.value = -1
    activeSuggestionsField.value = fieldName

    const query = fields[fieldName].trim()

    if (query.length < MIN_LENGTH[fieldName]) {
        suggestions.value = []
        hideSuggestions()
        return
    }

    debounceTimer = setTimeout(async () => {

        try {

            suggestions.value = await fetchSuggestions(
                query,
                API_TYPE[fieldName]
            )

            showSuggestions.value =
                suggestions.value.length > 0

            activeIndex.value = -1

        } catch (err) {
            console.error('API error:', err)
        }

    }, 250)
}


function navigate(direction) {

    if (!showSuggestions.value || !suggestions.value.length) {
        return
    }

    activeIndex.value =
        (
            activeIndex.value +
            direction +
            suggestions.value.length
        ) % suggestions.value.length
}


function selectActive() {

    if (
        activeIndex.value >= 0 &&
        suggestions.value[activeIndex.value]
    ) {
        selectSuggestion(
            suggestions.value[activeIndex.value],
            activeSuggestionsField.value
        )
    }
}


function selectSuggestion(feature, sourceField) {

    const p = feature.properties

    const address = {
        housenumber: p.housenumber ?? null,
        street: p.street ?? null,
        postcode: p.postcode ?? null,
        city: p.city ?? null,
        label: p.label ?? null,
        context: p.context ?? null,
        department: null,
        region: null,
        lat: feature.geometry.coordinates[1],
        lon: feature.geometry.coordinates[0],
    }

    if (address.context) {

        const parts =
            address.context
                .split(',')
                .map(s => s.trim())

        address.department = parts[1] ?? null
        address.region = parts[2] ?? null
    }


    if (sourceField === 'street') {

        fields.street = buildStreetLabel(p)
        setProgrammatic('postcode', p.postcode ?? '')
        setProgrammatic('city', p.city ?? '')

    } else if (sourceField === 'postcode') {

        fields.postcode = p.postcode ?? ''
        setProgrammatic('city', p.city ?? '')

    } else if (sourceField === 'city') {

        fields.city = p.city ?? ''
        setProgrammatic('postcode', p.postcode ?? '')
    }


    selectedAddress.value = address

    lastValidAddress.street = fields.street
    lastValidAddress.postcode = fields.postcode
    lastValidAddress.city = fields.city

    hideSuggestions()

    emits('selected-address', address)
}


function setProgrammatic(fieldName, value) {

    programmatic[fieldName] = true
    fields[fieldName] = value
}


function buildStreetLabel(p) {

    const parts = []

    if (p.housenumber) {
        parts.push(p.housenumber)
    }

    if (p.street) {
        parts.push(p.street)
    } else if (p.name) {
        parts.push(p.name)
    }

    return parts.join(' ')
}


function onBlur(event) {

    const field = event.target.id?.split('-').pop()

    if (
        field &&
        fields[field].trim() === ''
    ) {
        reset()
    }
    else if (
        selectedAddress.value &&
        (
            fields.street !== lastValidAddress.street ||
            fields.postcode !== lastValidAddress.postcode ||
            fields.city !== lastValidAddress.city
        )
    ) {
        setProgrammatic(
            'street',
            lastValidAddress.street
        )

        setProgrammatic(
            'postcode',
            lastValidAddress.postcode
        )

        setProgrammatic(
            'city',
            lastValidAddress.city
        )
    }

    emits('blur', event)

    clearTimeout(debounceTimer)

    hideSuggestions()
}


function hideSuggestions() {

    showSuggestions.value = false
    activeIndex.value = -1
}


async function prefill(value) {

    try {

        let features =
            await fetchSuggestions(
                value,
                'housenumber'
            )

        if (!features.length) {

            features =
                await fetchSuggestions(
                    value,
                    'municipality'
                )
        }

        if (features[0]) {
            selectSuggestion(
                features[0],
                'street'
            )
        }

    } catch (err) {
        console.error('Prefill error:', err)
    }
}


function reset() {

    setProgrammatic('street', '')
    setProgrammatic('postcode', '')
    setProgrammatic('city', '')

    lastValidAddress.street = ''
    lastValidAddress.postcode = ''
    lastValidAddress.city = ''

    selectedAddress.value = null

    suggestions.value = []

    hideSuggestions()

    emits('selected-address', null)
}


defineExpose({ prefill, reset })
</script>

<style scoped>
.address-fields {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
}

.address-row {
    display: flex;
    gap: 0.5rem;
}

.field-group {
    position: relative;
}

.field-address  { flex: 1; }
.field-postcode { width: 130px; flex-shrink: 0; }
.field-city     { width: 100%; }

input {
    width: 100%;
    box-sizing: border-box;
}

.suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    background: white;
    border: 1px solid #ccc;
    border-top: none;
    border-radius: 0 0 4px 4px;
    z-index: 1000;
    max-height: 250px;
    overflow-y: auto;
}

.suggestions li {
    padding: 8px 10px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.suggestions li:hover,
.suggestions li.active {
    background-color: #f0f0f0;
}

.suggestion-main {
    font-size: 14px;
    font-weight: 500;
    color: #111;
}

.suggestion-sub {
    font-size: 12px;
    color: #666;
}

input:read-only {
    background-color: #f5f5f5;
    cursor: not-allowed;
}
</style>